<template>
  <tr class="transition overflow-hidden">
    <td class="w-50 md:min-w-[120px] mobile-only:!hidden">
      <div class="bg-gray-50 aspect-square">
        <MediaImage v-if="image" v-bind="image" :image-style="imageStyle" />
      </div>
    </td>
    <td class="w-full mobile-only:mb-10">
      <NuxtLink :to="purchasedEntity?.url?.path" class="flex flex-col">
        <div v-if="productTitle" class="text-lg font-semibold">
          {{ productTitle }}
        </div>
        <div v-if="title">{{ title }}</div>
      </NuxtLink>
    </td>

    <td class="whitespace-nowrap w-50 md:w-100 min-w-[100px]">
      <FormKit
        v-model="localQuantity"
        min="0"
        step="1"
        type="number"
        @blur="onQuantityChange"
      />
    </td>

    <td>
      <button
        v-if="id"
        class="w-50 h-50 flex justify-center items-center text-green hover:text-green-alt"
        @click="$emit('remove', id)"
      >
        <SpriteSymbol name="delete" class="is-text-sized" />
      </button>
    </td>

    <td class="md:text-right ml-auto md:min-w-[120px]">
      <div class="relative">
        <span>{{ unitPrice?.formatted }}</span>
      </div>
    </td>

    <td class="md:text-right ml-auto md:min-w-[120px]">
      <div class="relative">
        <span>{{ totalPrice?.formatted }}</span>
        <span
          v-if="reducedTax"
          class="inline-block ml-3 font-medium"
          :title="reducedTax.adjustment?.label"
          >*</span
        >
      </div>
    </td>
  </tr>
</template>

<script lang="ts" setup>
import { TAX_REDUCED } from '@pharmasuisse/helpers/commerce'
import type { CartOrderItemFragment } from '#graphql-operations'

const emit = defineEmits<{
  (e: 'remove', id: string): void
  (e: 'changeQuantity', data: { id: string; quantity: number }): void
}>()

await initFormKit()

const props = defineProps<{
  /**
   * The ID of the order item.
   */
  id?: CartOrderItemFragment['id']

  /**
   * The title of the order item.
   */
  title?: CartOrderItemFragment['title']

  /**
   * The purchased quantity.
   */
  quantity?: CartOrderItemFragment['quantity']

  /**
   * The adjustments.
   */
  adjustments?: CartOrderItemFragment['adjustments']

  /**
   * The unit price of the order item.
   */
  unitPrice?: CartOrderItemFragment['unitPrice']

  /**
   * The total price of the order item. Usually quantity *
   * purchasedEntity.price.
   */
  totalPrice?: CartOrderItemFragment['totalPrice']

  /**
   * The purchased entity. Usually a product variation.
   */
  purchasedEntity?: CartOrderItemFragment['purchasedEntity']
}>()

function numOrString(v: number | string): number {
  if (typeof v === 'number') {
    return v
  }
  const parsed = parseInt(v)
  if (Number.isNaN(parsed)) {
    return 0
  }

  return parsed
}

// Workaround to keep the quantity in the FormKit input in sync.
const localQuantity = ref((props.quantity || 1).toString())

// Watch the props and update the local quantity value with the value from the
// prop.
watch(props, (updatedProps) => {
  localQuantity.value = (updatedProps.quantity || 1).toString()
})

const image = computed(() => {
  return props.purchasedEntity?.image
})

const productTitle = computed(() => {
  return props.purchasedEntity?.product?.title
})

/**
 * Determine if the order item has a reduced tax rate.
 */
const reducedTax = computed(() => {
  return props.adjustments?.find((v) => v.adjustment?.sourceId === TAX_REDUCED)
})

/**
 * Handler when changing the quantity.
 */
function onQuantityChange() {
  if (props.quantity?.toString() !== localQuantity.value) {
    emit('changeQuantity', {
      id: props.id || '',
      quantity: numOrString(localQuantity.value),
    })
  }
}

const imageStyle = defineImageStyle({
  type: 'sizes',
  aspectRatio: 1,
  sizes: {
    sm: 50,
    lg: 120,
  },
})
</script>
